import React, { useEffect, useMemo, useRef, useState } from 'react';
import './BadgePills.css';
import api from '../../api';
import { BADGE_DISPLAY } from '../../constants/badges';

const ORDER = [
  'BARTER_KING',
  'MERCHANT_STAR',
  'CREATOR_FLAME',
  'PI_GUARDIAN',
  'COUNCIL',
  'PI_PIONEER',
  'INNOVATOR',
];

function normalizeFromApi(payload) {
  const arr = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.items)
    ? payload.items
    : Array.isArray(payload?.users)
    ? payload.users
    : [];

  return arr
    .map((it) => {
      const badge = String(it.badge || it.type || it.id || '').toUpperCase();
      const count = Number(it.count ?? it.qty ?? 1);
      return badge ? { badge, count: Number.isFinite(count) ? count : 1 } : null;
    })
    .filter(Boolean);
}

function normalizeFromProp(badgesProp) {
  if (!Array.isArray(badgesProp)) return [];
  return badgesProp
    .map((b) => {
      const badge = String(b?.type || '').toUpperCase();
      const count = Number(b?.level ?? 1);
      return badge ? { badge, count: Number.isFinite(count) ? count : 1 } : null;
    })
    .filter(Boolean);
}

export default function BadgePills({
  username,
  badges: badgesProp,
  max = 6,
  className = '',
  stacked = false,
  animate = true, // NEW: enable animations by default
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const pillRefs = useRef([]); // for intersection-based reveal

  useEffect(() => {
    let mounted = true;

    (async () => {
      if (Array.isArray(badgesProp)) {
        const normalized = normalizeFromProp(badgesProp);
        if (mounted) {
          setItems(normalized);
          setLoading(false);
        }
        return;
      }

      try {
        setLoading(true);
        const url = username
          ? `/badges/held/${encodeURIComponent(username)}`
          : `/badges/held/me`;
        const res = await api.get(url);
        if (!mounted) return;
        setItems(normalizeFromApi(res?.data));
      } catch {
        if (mounted) setItems([]);
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [username, badgesProp]);

  const decorated = useMemo(() => {
    const withMeta = items
      .map(({ badge, count }) => {
        const meta = BADGE_DISPLAY[badge] || {};
        if (!meta?.label) return null;
        return {
          id: badge,
          label: meta.label,
          icon: meta.icon,
          emoji: meta.emoji,
          count,
        };
      })
      .filter(Boolean);

    withMeta.sort((a, b) => {
      const ai = ORDER.indexOf(a.id);
      const bi = ORDER.indexOf(b.id);
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    });

    return withMeta.slice(0, max);
  }, [items, max]);

  // IntersectionObserver to add the "--in" class when visible (staggered)
  useEffect(() => {
    if (!animate) return;
    const prefersReduced =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReduced) return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('badge-pill--in');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );

    pillRefs.current.forEach((el) => el && io.observe(el));
    return () => io.disconnect();
  }, [decorated, animate]);

  if (loading) {
    return (
      <div className={`badge-pills ${className}`}>
        <div className="badge-pill skeleton" />
        <div className="badge-pill skeleton" />
      </div>
    );
  }

  if (!decorated.length) return null;

  return (
    <div
      className={`badge-pills ${stacked ? 'stacked' : ''} ${
        animate ? 'with-anim' : ''
      } ${className}`}
      role="list"
      aria-label="Badges"
    >
      {decorated.map((b, idx) => (
        <div
          key={b.id}
          className="badge-pill"
          role="listitem"
          title={b.count > 1 ? `${b.label} (x${b.count})` : b.label}
          style={{ '--i': idx }} // used for staggered delay
          ref={(el) => (pillRefs.current[idx] = el)}
        >
          {b.icon ? (
            <img className="badge-pill-icon" src={b.icon} alt="" aria-hidden />
          ) : (
            <span className="badge-pill-emoji" aria-hidden>
              {b.emoji}
            </span>
          )}
          <span className="badge-pill-text">
            {b.label}
            {b.count > 1 ? ` (x${b.count})` : ''}
          </span>
        </div>
      ))}
    </div>
  );
}
